import { z } from "zod";
import { ENROLLMENT_DAYS_MAX, ENROLLMENT_DAYS_MIN, LATE_SUBMISSION_DAYS_MAX, LATE_SUBMISSION_DAYS_MIN, REVIEW_DUE_DATE_OFFSET_MAX, REVIEW_DUE_DATE_OFFSET_MIN } from "./reviewConstraints.ts";
import { CycleReviewTypes } from "./reviewTypes.ts";

export const CycleScheduleTypes = ["focal", "anniversary", "milestone"] as const;

export type CycleScheduleType = (typeof CycleScheduleTypes)[number];

export const CycleCohortIntervals = ["weekly", "monthly"] as const;

export type CycleCohortInterval = (typeof CycleCohortIntervals)[number];

export const MilestoneStartOffsets = [30, 60, 90, 180, 365] as const;

export type MilestoneStartOffset = (typeof MilestoneStartOffsets)[number];

export const EvaluationPeriodStartOffsets = [-30, -60, -90, -120, -180, -365] as const;

export type EvaluationPeriodStartOffset = (typeof EvaluationPeriodStartOffsets)[number];

const DateOnlySchema = z.iso.date();

const CycleReviewWindowSchema = z.object({
    startOffset: z.number().int(),
    dueDateOffsets: z.partialRecord(z.enum(CycleReviewTypes), z.number().int()),
    evaluationPeriodStartOffset: z.number().int().optional(),
    lockAfterDueDateOffset: z.number().int().nullable()
});

export type CycleReviewWindow = z.infer<typeof CycleReviewWindowSchema>;

export const CycleScheduleSchema = z.object({
    type: z.enum(CycleScheduleTypes),
    isAutomatedStart: z.boolean(),
    automatedStartDate: DateOnlySchema.optional(),
    isAutomatedLockEnabled: z.boolean(),
    automatedLockDate: DateOnlySchema.optional(),
    cohortInterval: z.enum(CycleCohortIntervals).optional(),
    intakeEndDateOffset: z.number().int().optional(),
    isRecurring: z.boolean(),
    reviewWindow: CycleReviewWindowSchema.optional()
});

export type CycleSchedule = z.infer<typeof CycleScheduleSchema>;

export const CycleDueDatesSchema = z.partialRecord(z.enum(CycleReviewTypes), DateOnlySchema);

export type CycleDueDates = z.infer<typeof CycleDueDatesSchema>;

export const CycleScheduleActionSchemas = [
    z.object({ type: z.literal("UpdateReviewCycleFocalScheduleAction") }),
    z.object({ type: z.literal("UpdateReviewCycleAnniversaryScheduleAction") }),
    z.object({ type: z.literal("UpdateReviewCycleMilestoneScheduleAction"), startOffset: z.literal(MilestoneStartOffsets) }),
    z.object({ type: z.literal("UpdateReviewCycleAutomatedStartAction"), isAutomatedStart: z.boolean(), startDate: DateOnlySchema.optional() }),
    z.object({ type: z.literal("UpdateReviewCycleAutomatedLockAction"), isAutomatedLockEnabled: z.boolean(), lockDate: DateOnlySchema.optional() }),
    z.object({
        type: z.literal("UpdateReviewCycleIntakeEndDateOffsetAction"),
        intakeEndDateOffset: z.number().int().min(ENROLLMENT_DAYS_MIN).max(ENROLLMENT_DAYS_MAX)
    }),
    z.object({ type: z.literal("UpdateReviewCycleCohortIntervalAction"), cohortInterval: z.enum(CycleCohortIntervals).optional() }),
    z.object({ type: z.literal("UpdateReviewCycleRecurrenceAction"), isRecurring: z.boolean() }),
    z.object({
        type: z.literal("UpdateReviewCycleReviewDueDateOffsetAction"),
        reviewType: z.enum(CycleReviewTypes),
        dueDateOffset: z.number().int().min(REVIEW_DUE_DATE_OFFSET_MIN).max(REVIEW_DUE_DATE_OFFSET_MAX)
    }),
    z.object({ type: z.literal("UpdateReviewCycleReviewDueDateAction"), reviewType: z.enum(CycleReviewTypes), dueDate: DateOnlySchema.optional() }),
    z.object({ type: z.literal("UpdateReviewCycleEvaluationPeriodAction"), startDate: DateOnlySchema.optional(), endDate: DateOnlySchema.optional() }),
    z.object({ type: z.literal("UpdateReviewCycleEvaluationPeriodStartOffsetAction"), startOffset: z.literal(EvaluationPeriodStartOffsets).optional() }),
    z.object({
        type: z.literal("UpdateReviewCycleLockAfterDueDateOffsetAction"),
        lockAfterDueDateOffset: z.number().int().min(LATE_SUBMISSION_DAYS_MIN).max(LATE_SUBMISSION_DAYS_MAX).nullable()
    })
] as const;
